import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EmployeeCrudDTO, TEAM_SORT } from 'src/app/domain/company/dto/EmployeeCRUDDTO';
import { Employee, EmployeeAccessLevel } from 'src/app/domain/organization/dto/Employee';
import { CURRENT_ROLES, hasAdminPDFAccess, hasAdminPortalAccess, hasMarketAnalysisAccess, hasDataRoomAccess } from "../../../../recovery/util/employee-recovery.util";
import { EmployeeService } from '../../../service/employee.service';
import { Organization } from 'src/app/domain/organization/dto/Organization';
import { firstValueFrom, Subject } from 'rxjs';
import { OrganizationAccesses } from '../../service/organizations.service';
import { AuthService, UserInfo } from '../../../../auth/service/auth.service';
import { ICON_FOR_USER_ROLE } from "../team-modal-crud/team-modal-crud.component";
import { filter, takeUntil, tap } from "rxjs/operators";

@Component({
  selector: 'app-team-table',
  templateUrl: './team-table.component.html',
  styleUrls: ['./team-table.component.scss'],
})
export class TeamTableComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  @Input() usersTable: UsersTable[];
  @Input() selectedOrganization: Organization;
  @Input() isAdmin = false;
  @Input() orgAccess: OrganizationAccesses;
  @Input() organizationAccountId: number | null = null;
  @Output() chosenUser = new EventEmitter<EmployeeCrudDTO>();
  @Output() threeDotsClicked = new EventEmitter<ThreeDotsMenuEmployeeFields>();
  @Output() updateTeamMemberByPortalAdminAccess = new EventEmitter<Employee>();

  employeeCheckboxFields = new EmployeeCheckboxFields();

  iconForUserRoles = ICON_FOR_USER_ROLE;
  currentRoles = CURRENT_ROLES;
  roles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES.Viewer, CURRENT_ROLES['No Access']];
  portalAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES['No Access']];
  kpiAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES.Viewer, CURRENT_ROLES['No Access']];
  pdfAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES['No Access']];
  marketAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Viewer, CURRENT_ROLES['No Access']];
  financialStatementAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES.Viewer, CURRENT_ROLES['No Access']];
  dataRoomAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES['No Access']];
  hasPortalAccess = hasAdminPortalAccess;
  hasPDFAccess = hasAdminPDFAccess;
  hasMarketAnalysisAccess = hasMarketAnalysisAccess;
  hasDataRoomAccess = hasDataRoomAccess;
  showTooltip = false;
  tooltipCoordinates: DOMRect;
  toolTipTitle = 'This user is organization admin';
  currentUser: UserInfo;
  MAX_DESC_LEN = 15;
  NOT_IN_PLAN_TEXT = 'Not in plan';
  threeDotsMenuFields: ThreeDotsMenuEmployeeFields = {
    edit: false,
    delete: false,
    resendInvite: false,
  };

  readonly teamSort = TEAM_SORT;

  constructor(
    private employeeService: EmployeeService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.initSubscription();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  chosenUserEmitter(t: EmployeeCrudDTO): void {
    this.chosenUser.emit(t);
  }

  threeDotsClickedEmitter(): void {
    this.threeDotsClicked.emit(this.threeDotsMenuFields);
    this.threeDotsMenuFields = {
      edit: false,
      delete: false,
      resendInvite: false,
    };
  }

  async updateCheckBoxInDB(employee: Employee, field: EmployeeCheckBoxByTable, hasAccess: boolean): Promise<void> {
    await this.employeeService.updateCheckBox(employee.id, field, hasAccess);
    // After successful update.
    switch (field) {
      case 'portfolio-notifications':
        employee.portfolioNotification = hasAccess;
        break;
      case 'admin-portal':
        employee.portalAdminAccess = hasAccess;
        employee.portalPdf = hasAccess;
        this.moveToAnotherTable(employee);
        break;
      case 'portal-pdf':
        employee.portalPdf = hasAccess;
        this.moveToAnotherTable(employee);
        break;
      case 'market-analysis':
        employee.marketAnalysis = hasAccess;
        this.moveToAnotherTable(employee);
        break;
      case 'data-room':
        employee.dataRoomAccess = hasAccess;
        this.moveToAnotherTable(employee);
        break;
      case 'kpi-notification':
        employee.kpiNotification = hasAccess;
        break;
    }
    this.moveToAnotherTable(employee);
  }

  async updateAccessLevel(employee: Employee, value: EmployeeAccessLevel): Promise<void> {
    if (employee.accessLevel === value) {
      return;
    }
    await this.employeeService.updateAccessLevel(employee.id, value);
    employee.accessLevel = value;
    this.moveToAnotherTable(employee);
  }

  /**
   * Check employee accesses and (if needed) move selected employee to table "With access" or "Without access"
   *
   * @param employee contains employee accesses to services
   */
  moveToAnotherTable(employeeObj: Employee): void {
    const employee = Employee.deserialize(employeeObj);

    if (employee.hasNoAccess()) {
      const accessTable = this.usersTable[0];
      if (accessTable.users.some((el) => el.id === employee.id)) {
        accessTable.users = accessTable.users.filter((el) => el.id !== employee.id);
        this.usersTable[1].users.push(employee);
      }
    } else if (employee.hasAtLeastOneAccess()) {
      const noAccessTable = this.usersTable[1];
      if (noAccessTable.users.some((el) => el.id === employee.id)) {
        noAccessTable.users = noAccessTable.users.filter((el) => el.id !== employee.id);
        this.usersTable[0].users.push(employee);
      }
    }
  }

  async updateKpiAccessLevel(employee: Employee, value: EmployeeAccessLevel): Promise<void> {
    if (employee.kpiAccessLevel === value) {
      return;
    }
    await firstValueFrom(this.employeeService.updateKpiAccessLevel(employee.id, value));
    employee.kpiAccessLevel = value;
    this.moveToAnotherTable(employee);
  }

  async updateFinancialStatementAccessLevel(employee: Employee, accessLevel: EmployeeAccessLevel): Promise<void> {
    if (employee.financialStatementAccessLevel === accessLevel) {
      return;
    }
    await firstValueFrom(this.employeeService.updateFinancialStatementAccessLevel(employee.id, accessLevel));
    employee.financialStatementAccessLevel = accessLevel;
    this.moveToAnotherTable(employee);
  }

  sendEmail(email: string): void {
    window.open(`mailto:${email}`);
  }

  activateTooltipWithOrgAdmin(id: string, isOrgAdm: boolean): void {
    if (!isOrgAdm) {
      return;
    }
    this.toolTipTitle = ORG_ADMIN_TOOLTIP_TEXT;
    this.showTooltip = true;
    this.tooltipCoordinates = document.getElementById(id).getBoundingClientRect();
  }

  activateTooltip(id: string, toolTipTitle: string): void {
    if (toolTipTitle.length < this.MAX_DESC_LEN) {
      return;
    }
    this.toolTipTitle = toolTipTitle;
    this.showTooltip = true;
    this.tooltipCoordinates = document.getElementById(id).getBoundingClientRect();
  }

  private initSubscription(): void {
    this.authService.userInfo
      .pipe(
        filter((userInfo: UserInfo): boolean => Boolean(userInfo)),
        tap((userInfo: UserInfo): UserInfo => this.currentUser = userInfo),
        takeUntil(this._destroyStream)
      )
      .subscribe();
  }
}

export type EmployeeCheckBoxByTable = 'org-admin' | 'portfolio-notifications' |
'admin-portal' | 'portal-pdf' | 'market-analysis' | 'kpi-notification' | 'kpis' |
'data-room';

export interface UsersTable {
  users: EmployeeCrudDTO[];
  type: 'With access' | 'Without access';
  opened: boolean;
}

export class EmployeeCheckboxFields {
  portfolioNotification: EmployeeCheckBoxByTable = 'portfolio-notifications';
  kpiNotification: EmployeeCheckBoxByTable = 'kpi-notification';
  adminPortal: EmployeeCheckBoxByTable = 'admin-portal';
  marketAnalysis: EmployeeCheckBoxByTable = 'market-analysis';
  portalPdf: EmployeeCheckBoxByTable = 'portal-pdf';
  dataRoom: EmployeeCheckBoxByTable = 'data-room';
}

export interface EmployeeRole {
  role: EmployeeAccessLevel;
  displayRole?: string; // only in frontend. It is used e.g. to show '-' instead of 'No access'
  iconNameAndStylingLeft?: string;
  iconNameAndStyling1?: string;
}

export interface ThreeDotsMenuEmployeeFields {
  edit: boolean;
  delete: boolean;
  resendInvite: boolean;
}

const ORG_ADMIN_TOOLTIP_TEXT = 'Manages users & organization entities';
